import apiService from '@sonifi/wb-core/services/api.service';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import {
  INCOMPLETE_STATUSES,
  isCanceledJob,
  isIncompleteJob,
  isSuccessfulJob,
  SUCCESS_STATUSES,
} from '@sonifi/wb-core/services/jobsHelper';
import {
  addTrailingSlash,
  parseQueryParams,
} from '@sonifi/wb-core/utils/parseHelper';
import { updateJobsStatus } from '../store/hotelDirectorySlice';

const pollingTime = 60 * 1000; // 1 minute;
const publishedStatusesFilter = Object.freeze({
  status__in: SUCCESS_STATUSES.concat(INCOMPLETE_STATUSES).join(','),
});

const getPublishJob = async (jobId) => {
  try {
    const url = `${ENDPOINTS.JOBS}/${jobId}/versions/1`;
    const res = await apiService.get(url);
    return res.data;
  } catch (error) {
    return {};
  }
};

const getLatestPublishJob = async ({ uri, overrides = null }) => {
  try {
    const params = {
      _order_by: '-created',
      rpp: 1,
      targets__contains: addTrailingSlash(uri),
      type: 'PUBLISH_COMPENDIUM',
      ...overrides,
    };

    const url = `${ENDPOINTS.JOBS}?${parseQueryParams(params)}`;
    const res = await apiService.get(url);

    const latestPublishJob = res.data.objects.length
      ? {
        ...res.data.objects[0],
        targets: res.data.objects[0].targets?.length
          ? [res.data._embedded[res.data.objects[0].targets[0]]]
          : [],
      }
      : null;
    return latestPublishJob;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

let pollingTimeout = null;

export const checkCleanPollingTimeout = () => {
  if (pollingTimeout) {
    clearTimeout(pollingTimeout);
  }
};

const setupPollingTimeout =
  ({ compendiaMenuUri }) =>
    async (dispatch) => {
      checkCleanPollingTimeout();
      pollingTimeout = setTimeout(async () => {
        const {
          latestPublishJob,
          lastPublishJob,
          lastPublished,
          lastPublishUser,
        } = await dispatch(getJobsPublishStatus({ compendiaMenuUri }));
        dispatch(
          updateJobsStatus({
            latestPublishJob,
            lastPublishJob,
            lastPublished,
            lastPublishUser,
          }),
        ); // Review when's publishing
      }, pollingTime);
    };

export const processLatestPublishJob =
  ({ job, compendiaMenuUri }) =>
    async (dispatch) => {
      if (!job) {
        return {
          lastPublishUser: null,
          lastPublishJob: null,
        };
      } else if (isIncompleteJob(job) || isCanceledJob(job)) {
        dispatch(setupPollingTimeout({ compendiaMenuUri }));
      }

      const lastPublishJob = {
        ...job,
        created: job.created,
      };

      const versionedJob = await getPublishJob(job.id);
      return {
        lastPublishUser: versionedJob._edited_by,
        lastPublishJob,
      };
    };

export const getJobsPublishStatus =
  ({ compendiaMenuUri }) =>
    async (dispatch) => {
      try {
        const response = {
          latestPublishJob: null,
          lastPublishJob: null,
          lastPublished: null,
          lastPublishUser: null,
        };

        const job = await getLatestPublishJob({ uri: compendiaMenuUri });

        const { lastPublishJob, lastPublishUser } = await dispatch(
          processLatestPublishJob({
            job,
            compendiaMenuUri,
          }),
        );

        response.lastPublishUser = lastPublishUser;
        response.lastPublishJob = lastPublishJob;

        if (isSuccessfulJob(lastPublishJob) || isIncompleteJob(lastPublishJob)) {
          response.latestPublishJob = lastPublishJob;
          response.lastPublished = lastPublishJob.created;
        } else {
          const latestPublishJob = await getLatestPublishJob({
            uri: compendiaMenuUri,
            overrides: publishedStatusesFilter,
          });
          response.latestPublishJob = latestPublishJob;
          response.lastPublished = response.latestPublishJob?.targets?.length
            ? response.latestPublishJob.targets[0].last_updated
            : null;
        }

        return response;
      } catch (error) {
        return Promise.reject(error);
      }
    };
